<template>
  <div
    v-if="popupState"
    id="popup-component"
    ref="modal"
    class="modal-mask fixed z-[9998] top-0 left-0 w-full h-full table"
    :class="!sidebarVariant && 'bg-[rgba(0,0,0,0.5)]'"
  >
    <div :class="!sidebarVariant ? 'modal-wrapper table-cell align-middle' : 'h-full'" @mousedown.prevent="closePopup">
      <div
        role="dialog"
        aria-modal="true"
        :aria-label="popupHeader"
        :class="`modal-content bg-foreground box-shadow-static-lg px-16 pb-16 w-full rounded-t-xl bottom-0 fixed sm:static sm:m-auto sm:rounded-xl overflow-auto ${sidebarClass} ${widthClass} ${customClasses}`"
        @mousedown.stop
      >
        <div class="bg-foreground sticky top-0 z-[1]">
          <div class="flex py-16">
            <h2 :class="`${headerClass} text-grey-header`">{{ popupHeader }}</h2>
            <button class="primary-focus ml-auto" aria-label="Close" @click="closePopup">
              <CloseIcon color="#4B4C52" aria-hidden="true" />
            </button>
          </div>
          <hr v-if="hasHeaderDivider" class="border-grey-divider-subdued -mx-16" />
        </div>
        <slot />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import CloseIcon from '../../graphics/icons/CloseIcon.vue';

export default {
  name: 'Popup',
  components: {
    CloseIcon,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    popupHeader: {
      type: String,
      default: '',
      required: true,
    },
    popupSize: {
      type: String,
      default: 'sm',
    },
    headerClass: {
      type: String,
      default: 'text-h4',
    },
    customClasses: {
      type: String,
      default: '',
    },
    hasHeaderDivider: {
      type: Boolean,
      default: false,
    },
    sidebarVariant: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      popupState: this.value,
      popupInitialized: false,
      tabKeydownListener: null as EventListener | null,
      escapeKeydownListener: null as EventListener | null,
    };
  },
  computed: {
    widthClass(): string {
      return this.popupSize === 'lg' ? 'sm:max-w-664' : 'sm:max-w-368';
    },
    sidebarClass(): string {
      return this.sidebarVariant ? 'sidebar-variant overflow-y-hidden' : 'max-h-[85vh]';
    },
  },
  watch: {
    value(parentVal: boolean) {
      this.popupState = parentVal;
    },
    popupState(value: boolean) {
      this.$emit('input', value);
      if (value) {
        this.openPopup();
      } else {
        this.closePopup();
      }
    },
  },
  beforeUpdate() {
    if (this.popupState) {
      document.activeElement?.classList.add('modal-trigger');
    }
  },
  beforeDestroy() {
    this.removeKeydownListeners();
  },
  updated() {
    if (this.popupState && !this.popupInitialized) {
      const modal = this.$refs.modal as HTMLElement;
      const focusableEls = modal?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      const firstFocusable = focusableEls[0] as HTMLElement;
      const lastFocusable = focusableEls[focusableEls.length - 1] as HTMLElement;
      this.addModalTabLoop(firstFocusable, lastFocusable);
      this.popupInitialized = true;
    }
  },
  methods: {
    openPopup() {
      this.popupState = true;
    },
    closePopup() {
      this.popupState = false;
      this.popupInitialized = false;
      this.$emit('close');
      (document.querySelector('.modal-trigger') as HTMLElement)?.focus();
      this.removeKeydownListeners();
    },
    addModalTabLoop(firstFocus: HTMLElement, lastFocus: HTMLElement) {
      firstFocus?.focus();

      this.tabKeydownListener = ((e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        if (e.shiftKey) {
          if (document.activeElement === firstFocus) {
            lastFocus.focus();
            e.preventDefault();
          }
        } else if (document.activeElement === lastFocus) {
          firstFocus.focus();
          e.preventDefault();
        }
      }) as EventListener;

      this.escapeKeydownListener = ((e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closePopup();
        }
      }) as EventListener;

      document.addEventListener('keydown', this.tabKeydownListener);
      document.addEventListener('keydown', this.escapeKeydownListener);
    },
    removeKeydownListeners() {
      if (this.tabKeydownListener) {
        document.removeEventListener('keydown', this.tabKeydownListener);
        this.tabKeydownListener = null;
      }
      if (this.escapeKeydownListener) {
        document.removeEventListener('keydown', this.escapeKeydownListener);
        this.escapeKeydownListener = null;
      }
    },
  },
};
</script>

<style>
/* hide cookie banner when popup is open */
main:has(#popup-component) ~ #onetrust-consent-sdk #ot-sdk-btn-floating {
  z-index: 1 !important;
}

.sidebar-variant {
  height: 100%;
  margin-right: 0;
  @media only screen and (min-width: 448px) {
    height: 100vh;
  }
}
</style>
